import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import memoize from "memoize-one";
import { Collapse, Icon } from 'antd';
import isEqual from 'lodash/isEqual';

import './index.scss';

import {
  getLiveChatsGroups,
  getUntaggedChatsId
} from '../../../../../../data/redux/chats_details/selectors';
import { joinChatItemByPsid } from '../../../../../../data/redux/chats_details/actions';

import { APP_LAYOUT, CHAT_LIST_TYPE } from '../../../../../../data/config/constants';
import { IconMessage } from '../../../../../../data/assets/icons';
import { IMG_EMPTY } from '../../../../../../data/assets/img';

import ChatItem from '../chatitem';
import UntaggedChatItem from '../untaggedchatitem';
import ShowMessage from '../../../../../../components/showmessage';
import ErrorBoundary from '../../../../../../components/errorboundary';

class ChatList extends React.Component {
  state = {
    active_panel: CHAT_LIST_TYPE.MY_CHAT,
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleShortcutsKeyDown);
  }

  componentDidUpdate(prevProps) {
    const { live_chats_group, untagged_chats_id } = this.props;
    if (!isEqual(prevProps.live_chats_group, live_chats_group)) {
      const active_panel = this.memoizedActivePanel(live_chats_group, untagged_chats_id.length === 0);
      if (this.state.active_panel !== active_panel)
        this.setState({ active_panel });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleShortcutsKeyDown);
  }

  handleShortcutsKeyDown = event => {
    const { live_chats_group, selected_active_chat_id, admin_id, actions } = this.props;
    if (event.shiftKey && event.keyCode === 38 && selected_active_chat_id) {
      const index = live_chats_group.my_chats.findIndex(psid => selected_active_chat_id === psid);
      if (index > 0) {
        const prev_chat_item_psid = live_chats_group.my_chats[index - 1];
        actions.joinChatItemByPsid(prev_chat_item_psid, admin_id);
      }
    } else if (event.shiftKey && event.keyCode === 40 && selected_active_chat_id) {
      const index = live_chats_group.my_chats.findIndex(psid => selected_active_chat_id === psid);
      if (index < (live_chats_group.my_chats.length - 1)) {
        const next_chat_item_psid = live_chats_group.my_chats[index + 1];
        actions.joinChatItemByPsid(next_chat_item_psid, admin_id);
      }
    }
  };

  memoizedTotalOffsetHeight = memoize((group, is_empty_untagged_list) => {
    const height = APP_LAYOUT.APP_HEADER_HEIGHT + APP_LAYOUT.CHAT_LIST_HEADER_HEIGHT + (group.pending_chats.length !== 0 ? 55 : 0) + (group.in_queue_chats.length !== 0 ? 55 : 0) + (group.my_chats.length !== 0 ? 55 : 0) + (group.agent_chats.length !== 0 ? 55 : 0) + (group.supervised_chats.length !== 0 ? 55 : 0) + (group.bot_handling_chats.length !== 0 ? 55 : 0) + (is_empty_untagged_list ? 0 : 55);
    return height;
  });

  memoizedActivePanel = memoize((group, is_empty_untagged_list) => {
    let active_panel = CHAT_LIST_TYPE.MY_CHAT;
    if (group.my_chats.length !== 0)
      active_panel = CHAT_LIST_TYPE.MY_CHAT;
    else if (group.pending_chats.length !== 0)
      active_panel = CHAT_LIST_TYPE.PENDING;
    else if (!is_empty_untagged_list)
      active_panel = CHAT_LIST_TYPE.UNTAGGED_CHATS
    else if (group.agent_chats.length !== 0)
      active_panel = CHAT_LIST_TYPE.AGENT_CHATS;
    else if (group.supervised_chats.length !== 0)
      active_panel = CHAT_LIST_TYPE.SUPERVISED;
    else if (group.in_queue_chats.length !== 0)
      active_panel = CHAT_LIST_TYPE.IN_QUEUE;
    else if (group.bot_handling_chats.length !== 0)
      active_panel = CHAT_LIST_TYPE.BOT_HANDLING;
    return active_panel;
  });

  onChangePanelCollapse = active_panel => {
    this.setState({ active_panel });
  };

  renderChatItemsAccordian = (list, title, offset_height, icon_class) => {
    if (list.length !== 0) {
      const { active_panel } = this.state;
      const { screen_height } = this.props;
      return (
        <Collapse
          bordered={false}
          activeKey={active_panel}
          onChange={this.onChangePanelCollapse}
          accordion
        >
          <Collapse.Panel
            className="chatsPanel"
            header={
              <PanelHeader
                title={title}
                count={list.length}
                icon_class={icon_class}
              />
            }
            key={title}
          >
            <div
              className="ori-animated ori-fade-in ori-overflow-y-auto ori-overflow-x-hidden"
              style={{ maxHeight: `${screen_height - offset_height}px` }}
            >
              {
                list.map(id => {
                  if (title === CHAT_LIST_TYPE.UNTAGGED_CHATS)
                    return (
                      <ErrorBoundary key={`untagged-${id}`}>
                        <UntaggedChatItem id={id} />
                      </ErrorBoundary>
                    )
                  return (
                    <ErrorBoundary key={id}>
                      <ChatItem psid={id} />
                    </ErrorBoundary>
                  )
                })
              }
            </div>
          </Collapse.Panel>
        </Collapse>
      );
    }
  };

  render() {
    const {
      screen_width,
      screen_height,
      refreshChatList,
      chat_page_joining,
      live_chats_group,
      untagged_chats_id
    } = this.props;
    const is_mobile = screen_width < 768;
    const total_offset_height = this.memoizedTotalOffsetHeight(live_chats_group, untagged_chats_id.length === 0);
    // log('ChatList rendered');
    return (
      <div className="oriChatListContainer">
        <div
          className="ori-relative ori-tb-pad-5 ori-l-pad-15 ori-r-pad-30 ori-flex-column ori-flex-jc ori-b-border-light"
          style={{ height: `${APP_LAYOUT.CHAT_LIST_HEADER_HEIGHT}px` }}
        >
          <div
            className="ori-absolute ori-pad-5 ori-align-right-5 ori-font-light-hover-default"
            onClick={chat_page_joining ? null : refreshChatList}
          >
            <Icon type="sync" spin={chat_page_joining} />
          </div>
          <div className="ori-animated ori-fade-in ori-font-md"><span>Chat Stream</span></div>
        </div>
        {
          this.renderChatItemsAccordian(untagged_chats_id, CHAT_LIST_TYPE.UNTAGGED_CHATS, total_offset_height, "ori-font-violet")
        }
        {
          this.renderChatItemsAccordian(live_chats_group.pending_chats, CHAT_LIST_TYPE.PENDING, total_offset_height, "ori-font-danger")
        }
        {
          this.renderChatItemsAccordian(live_chats_group.in_queue_chats, CHAT_LIST_TYPE.IN_QUEUE, total_offset_height, "ori-font-warning")
        }
        {
          this.renderChatItemsAccordian(live_chats_group.my_chats, CHAT_LIST_TYPE.MY_CHAT, total_offset_height, "ori-font-primary")
        }
        {
          this.renderChatItemsAccordian(live_chats_group.agent_chats, CHAT_LIST_TYPE.AGENT_CHATS, total_offset_height, "ori-font-aquamarine")
        }
        {
          this.renderChatItemsAccordian(live_chats_group.supervised_chats, CHAT_LIST_TYPE.SUPERVISED, total_offset_height, "ori-font-info")
        }
        {
          this.renderChatItemsAccordian(live_chats_group.bot_handling_chats, CHAT_LIST_TYPE.BOT_HANDLING, total_offset_height, "ori-font-green")
        }
        {
          total_offset_height === (APP_LAYOUT.APP_HEADER_HEIGHT + APP_LAYOUT.CHAT_LIST_HEADER_HEIGHT) &&
          <div
            className="ori-flex-column ori-flex-jc"
            style={is_mobile ? {} : { height: `${screen_height - total_offset_height}px` }}
          >
            <ShowMessage
              img_src={IMG_EMPTY}
              title="No live conversation"
              message="No any live conversation is avaliable"
              height={140}
              width={140}
            />
          </div>
        }
      </div>
    );
  }
}

const PanelHeader = props => {
  const { icon_class, title, count } = props;
  return (
    <div className="ori-flex-row ori-flex-jb">
      <div className="ori-flex-row ori-full-flex">
        <div className="ori-flex-column ori-flex-jc">
          <IconMessage size={16} className={`ori-r-mrgn-5 ${icon_class}`} />
        </div>
        <p className="ori-capitalize">{title}</p>
      </div>
      <div className="ori-l-mrgn-5 ori-flex-column ori-flex-jc">
        <div className={"ori-lr-pad-5 ori-border-radius-10 ori-border-light ori-bg-default"}>{count}</div>
      </div>
    </div>
  );
}

const mapStateToProps = state => {
  return {
    screen_height: state.page_details.device_data.screen_height,
    screen_width: state.page_details.device_data.screen_width,
    admin_id: state.admin_details.admin_id,
    is_chats_socket_connected: state.chats_details.is_socket_connected,
    selected_active_chat_id: state.chats_details.selected_active_chat_id,
    chat_page_joining: state.chats_details.loaders.chat_page_joining,
    live_chats_group: getLiveChatsGroups(state),
    untagged_chats_id: getUntaggedChatsId(state)
  };
};

const mapDispatchToProps = dispatch => {
  return {
    actions: bindActionCreators(
      {
        joinChatItemByPsid
      },
      dispatch),
  };
};

PanelHeader.propTypes = {
  title: PropTypes.string,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon_class: PropTypes.string,
};

ChatList.propTypes = {
  screen_height: PropTypes.number,
  screen_width: PropTypes.number,
  is_chats_socket_connected: PropTypes.bool,
  selected_active_chat_id: PropTypes.string,
  chat_page_joining: PropTypes.bool,
  refreshChatList: PropTypes.func,
  live_chats_group: PropTypes.object,
  actions: PropTypes.object,
  admin_id: PropTypes.string,
  untagged_chats_id: PropTypes.array
};

ChatList.defaultProps = {
  chat_page_joining: false,
  untagged_chats_id: []
};

export default connect(mapStateToProps, mapDispatchToProps)(ChatList);



// WEBPACK FOOTER //
// ./src/app/pagecontainer/modules/chats/components/chatlist/index.js